import classnames from 'classnames';
import React, { FC, memo, useCallback, useLayoutEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Route, Switch } from 'react-router-dom';
import { Breadcrumbs, Search, Text, TextButton, ToggleButton, StorageSpaceToasts } from '../../../components/common';
import { EmptyTrash, NewFolder, SiteAdd } from '../../../components/common/WixIcons';
import { ModalName } from '../../../constants/ModalName';
import { RoutePath } from '../../../constants/Routes';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { TrackBoardClickOrigin, useTrackBoardAnalytics } from '../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useModalHandlersAndOptions } from '../../../utils/hooks/useModal';
import { isRootFolder } from '../../../utils/media-manager-sdk/items';
import { isBoards } from '../../../utils/media-manager-sdk/sources';
import { LayoutSwitcher } from '../LayoutSwitcher';
import { MediaTypeSelect } from '../MediaTypeSelect';
import { SortSwitcher } from '../SortSwitcher';
import styles from './TopBar.scss';
import { IOuter } from './TopBar.types';

const TopBarComponent: FC<IOuter> = ({
  source,
  emptyTrashButtonClicked,
  isHome,
  isEnterpriseTab = false,
  searchQuery,
  handleSearchEnterPressed,
  handleSearchClearButtonClick,
  handleDropOnFolder,
  searchBiParams,
  isMediaTypeSelectVisible,
  isSortSelectVisible,
  isLayoutSwitchVisible,
  isActionBarVisible,
  isEmptyTrashVisible,
  isCreateFolderVisible,
  layouts,
  layout,
  setLayout,
  mediaTypes,
  setMediaType,
  sort,
  setSort,
  folderPath,
  openFolder,
  createNewFolderStartFromTop,
  currentFolder,
}) => {
  const { formatMessage } = useIntl();
  const { mmgrSettings } = useMMGRSettings();
  const { isSecureContext } = mmgrSettings;
  const { openModal: openEditBoardModal } = useModalHandlersAndOptions(ModalName.EditBoard);
  const { trackCreateBoardClick } = useTrackBoardAnalytics();

  const isCreateNewBoardVisible = isRootFolder(currentFolder) && isBoards(source);

  const areRightActionsVisible = isMediaTypeSelectVisible || isSortSelectVisible || isLayoutSwitchVisible;
  const areLeftActionsVisible = isEmptyTrashVisible || isCreateFolderVisible || isCreateNewBoardVisible;
  const isDividerVisible = areRightActionsVisible && areLeftActionsVisible;

  const searchPlaceholderTranslationKey = isSecureContext
    ? 'search.input-placeholder'
    : 'search.input-placeholder-new-layout';
  const searchPlaceholder = formatMessage({ id: searchPlaceholderTranslationKey });
  const handleCreateBoardButtonClick = useCallback(() => {
    openEditBoardModal();

    trackCreateBoardClick(TrackBoardClickOrigin.TopBar);
  }, [openEditBoardModal, trackCreateBoardClick]);

  const topBarRef = useRef<HTMLDivElement>();
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--top-bar-height',
      `${topBarRef.current ? topBarRef.current.offsetHeight : 0}px`,
    );
  });

  return (
    <div
      className={classnames(styles.topBar, { [styles.enterpriseTabTopBar]: isEnterpriseTab })}
      data-hook="top-bar"
      ref={topBarRef}
    >
      {!isHome && !isEnterpriseTab && (
        <>
          <StorageSpaceToasts />
          <div className={classnames(styles.searchWrapper, styles.noBottomBorder)}>
            <div className={styles.fullWidthSearch}>
              <Search
                value={searchQuery}
                onEnterPressed={handleSearchEnterPressed}
                onClearButtonClick={handleSearchClearButtonClick}
                biParams={searchBiParams}
                isAlwaysActive
                placeholder={searchPlaceholder}
              />
            </div>
          </div>
        </>
      )}

      {isActionBarVisible && (
        <div className={styles.actionsBar} data-hook="actions-bar">
          <Switch>
            <Route path={RoutePath.Search}>
              <Text weight="bold" size="medium" className={styles.searchResults}>
                <FormattedMessage id="search.results" />
              </Text>
            </Route>
            <Route>
              <div className={styles.breadcrumbs}>
                <Breadcrumbs folderPath={folderPath} source={source} onClick={openFolder} onDrop={handleDropOnFolder} />
              </div>
            </Route>
          </Switch>

          <div className={styles.moreActions}>
            {isEmptyTrashVisible && (
              <ToggleButton
                className={styles.moreActionsButton}
                onClick={emptyTrashButtonClicked}
                dataHook="top-panel.empty-trash-button"
                labelValue={<FormattedMessage id="actions.empty-trash" />}
              >
                <EmptyTrash />
              </ToggleButton>
            )}

            {isCreateFolderVisible && (
              <ToggleButton
                className={styles.moreActionsButton}
                onClick={createNewFolderStartFromTop}
                dataHook="create-new-folder-button"
                labelValue={<FormattedMessage id="actions.create-new-folder" />}
              >
                <NewFolder />
              </ToggleButton>
            )}

            {isCreateNewBoardVisible && (
              <TextButton
                as="button"
                onClick={handleCreateBoardButtonClick}
                skin="dark"
                dataHook="create-new-board-button"
              >
                <div className={styles.createNewBoardAction}>
                  <SiteAdd className={styles.createNewBoardIcon} />
                  <FormattedMessage id="boards.teams.create-new-board" />
                </div>
              </TextButton>
            )}

            {isDividerVisible && <div className={styles.actionsDivider} />}

            {isMediaTypeSelectVisible && <MediaTypeSelect mediaTypes={mediaTypes} onMediaTypeSelect={setMediaType} />}

            {isSortSelectVisible && (
              <SortSwitcher className={styles.moreActionsButton} sort={sort} onSortChange={setSort} />
            )}

            {isLayoutSwitchVisible && (
              <LayoutSwitcher
                className={styles.moreActionsButton}
                layout={layout}
                onLayoutChange={setLayout}
                layouts={layouts}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const TopBar: FC<IOuter> = memo<IOuter>(TopBarComponent);
